import {Injectable, OnDestroy, Optional} from '@angular/core';

import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {AuthService, LockoutService, UserProfile} from 'auth-commons';
import {filter, startWith, takeUntil} from 'rxjs/operators';

/**
 * A single (asynchronous) setup callback, which will be called with profile about the logged-in user.
 */
export type SetupCallback = (profile: UserProfile) => Promise<unknown>;

/**
 * Service responsible of broadcasting login/logout events.
 */
@Injectable()
export class LoginService implements OnDestroy {

    private destroyed = new Subject<void>();
    private loginSubj: Subject<UserProfile>;
    private logoutSubj: Subject<void>;
    private userProfileSubj: BehaviorSubject<UserProfile>;
    private readonly setupPhases: SetupCallback[][];

    constructor(@Optional() private readonly authService: AuthService,
                private readonly lockoutService: LockoutService) {
        this.loginSubj = new Subject<UserProfile>();
        this.logoutSubj = new Subject<void>();
        // start from current profile (which can be `null`)
        this.userProfileSubj = new BehaviorSubject<UserProfile>(authService?.profile);
        // setup phases
        this.setupPhases = [];
        // lockout event is treated as "logout"
        lockoutService.events.pipe(takeUntil(this.destroyed)).subscribe(() => this.notifyLogout());
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    /**
     * Emits whenever a user's profile changes (e.g. due to login).
     */
    get userProfile(): Observable<UserProfile> {
        return this.userProfileSubj.asObservable();
    }

    /**
     * Emits whenever a user logs in.
     */
    get login(): Observable<UserProfile> {
        return this.loginSubj.asObservable();
    }

    /**
     * Emits whenever a user logs out.
     */
    get logout(): Observable<void> {
        return this.logoutSubj.asObservable();
    }

    /**
     * Adds a callback which will be called upon {@link UserProfile}'s login during the specified
     * phase (identified by an arbitrary progressive number). Every callback is expected to return
     * a promise which resolves after loading is complete. All callbacks within the same phase are
     * called in parallel, while all of them are waited for completion before moving to the next phase.
     * <br/>
     * NOTE: this method is meant to be called during startup (that is, within some service's constructor);
     * otherwise, some hooks may not  be called at proper time.
     * When all hooks have been added, a call to {@link enableSetupHooks} is needed.
     */
    addSetupHook(phase: number, cb: SetupCallback) {
        if (!this.setupPhases[phase]) {
            this.setupPhases[phase] = [cb];
        } else {
            this.setupPhases[phase].push(cb);
        }
    }

    /**
     * Enable calling of all previously added setup hooks upon user's login.
     * <br/>
     * NOTE: this method is meant to be called *after* all other calls to {@link addSetupHook} (e.g. when all
     * services' constructors have been called).
     */
    enableSetupHooks() {
        this.loginSubj.pipe(
            startWith(this.authService?.profile), // in case user is already logged-in
            filter(profile => profile?.canOperate), // keep only usable profiles
            takeUntil(this.destroyed)
        ).subscribe(profile => {
            // different phases are executed sequentially
            console.debug(`Performing system setup for user ${profile?.username}`);
            let setup = Promise.resolve();
            this.setupPhases.forEach(cbs => {
                // callbacks within same phase are executed in parallel
                setup = setup.then(() => Promise.all(cbs.map(cb => cb(profile))).then());
            });
            setup.then();
            console.debug(`Finished setup for user ${profile?.username}`);
        });
    }

    notifyLogin(profile: UserProfile) {
        this.loginSubj.next(profile);
        this.userProfileSubj.next(profile); // user profile implicitly changed!
    }

    notifyLogout() {
        this.logoutSubj.next();
        this.userProfileSubj.next(null); // user profile implicitly changed!
    }

    notifyProfileChange(profile: UserProfile) {
        this.userProfileSubj.next(profile);
    }
}
